import { ReactNode, useRef } from "react";

import {
  Menu,
  MenuButton,
  MenuHeading,
  MenuItem,
  MenuItems,
  MenuSection,
} from "@headlessui/react";
import { groupBy } from "lodash-es";
import { AnimatePresence } from "motion/react";
import { twJoin, twMerge } from "tailwind-merge";
import { useBoolean } from "usehooks-ts";

import { MotionDropdownPanel } from "src/base-components/MotionDropdownPanel";
import { DropdownItem, DropdownPlacement } from "src/design-system/Dropdown";
import { useOnClickOutside } from "src/hooks/usehooks-ts";

export type FixedDropDownKey = string | null;
export type Section = { key: string; displayText: ReactNode };

export type FixedPositionedDropdownElement<
  T,
  K extends FixedDropDownKey = string,
  Sections extends readonly Section[] | undefined = undefined,
> = {
  key: K;
  value: T;
  disabled?: boolean;
  helpText?: string;
  helpBody?: React.ReactNode;
} & (Sections extends readonly Section[]
  ? { sectionKey: Sections[number]["key"] }
  : object);

export type RenderValueFn<
  T,
  K extends FixedDropDownKey = string,
  Sections extends readonly Section[] | undefined = undefined,
> = (
  element: FixedPositionedDropdownElement<T, K, Sections> & {
    selected: boolean;
  },
  close: () => void,
) => ReactNode;

export type SingleValueDropdownProps<T, K extends FixedDropDownKey> = {
  multiple?: false;
  selected?: K;
  onSelect?: (keys: K, e: React.MouseEvent<HTMLSpanElement>) => void;
  renderButtonValue: (
    values: T | undefined,
    isOpen: boolean,
    close: () => void,
  ) => ReactNode;
};

export type MultipleValueDropdownProps<T, K extends FixedDropDownKey> = {
  multiple: true;
  selected?: K[];
  onSelect?: (keys: K[], e: React.MouseEvent<HTMLSpanElement>) => void;
  renderButtonValue: (
    values: T[],
    isOpen: boolean,
    close: () => void,
  ) => ReactNode;
};

export type FixedPositionedDropdownProps<
  T,
  K extends FixedDropDownKey = string,
  Sections extends readonly Section[] | undefined = undefined,
> = {
  elements: FixedPositionedDropdownElement<T, K, Sections>[];
  renderValue: RenderValueFn<T, K>;
  renderFooter?: (close: () => void) => ReactNode;
  renderHeader?: () => ReactNode;
  className?: string;
  buttonClassName?: string;
  placement?: DropdownPlacement;
  buttonDataLoc?: string;
  itemsClassNames?: string;
  disabled?: boolean;
  dataLoc?: string;
  listMatchesButtonWidth?: boolean;
  placementOffsetX?: number;
  sections?: Sections;
  portal?: boolean;
  sectionDividers?: boolean;
  error?: boolean;
} & (SingleValueDropdownProps<T, K> | MultipleValueDropdownProps<T, K>);

const menuSpanClassName = "block w-full";

export const FixedPositionedDropdown = <
  T,
  K extends FixedDropDownKey = string,
  Sections extends readonly Section[] | undefined = undefined,
>({
  disabled = false,
  sectionDividers = false,
  onSelect,
  elements,
  renderButtonValue,
  renderValue,
  renderFooter,
  selected,
  className,
  buttonClassName,
  placement = "bottom end",
  buttonDataLoc,
  itemsClassNames,
  renderHeader,
  dataLoc,
  listMatchesButtonWidth = true,
  multiple,
  placementOffsetX,
  sections,
  portal,
  error,
}: FixedPositionedDropdownProps<T, K, Sections>) => {
  const {
    value: open,
    setTrue: openDropdown,
    setFalse: close,
  } = useBoolean(false);

  const selectedKeys = (() => {
    if (Array.isArray(selected)) {
      return selected;
    }

    return selected ? [selected] : [];
  })();

  const selectedItems = elements.filter((element) =>
    selectedKeys.includes(element.key),
  );

  const getNewSelectedItems = (key: K) =>
    selectedKeys.includes(key)
      ? selectedKeys.filter((selectedKey) => selectedKey !== key)
      : [...selectedKeys, key];

  const renderItems = (
    elements: FixedPositionedDropdownElement<T, K, Sections>[],
  ) =>
    elements.map((element) => (
      <MenuItem
        key={element.key}
        as={DropdownItem}
        className={twJoin(
          "font-inter-normal-12px text-gray-800",
          element.disabled &&
            "cursor-default text-gray-400 hover:bg-transparent",
        )}
        disabled={element.disabled && !element.helpText}
        onClick={(e) => {
          if (element.disabled) {
            return;
          }

          if (multiple) {
            e.stopPropagation(); // avoids closing the menu when multi select is possible
            onSelect?.(getNewSelectedItems(element.key), e);
          } else {
            onSelect?.(element.key, e);
          }

          if (!e.isPropagationStopped()) {
            close();
          }
        }}
      >
        <span className={menuSpanClassName}>
          {renderValue(
            {
              ...element,
              selected: multiple
                ? Boolean(selectedKeys?.includes(element.key))
                : selectedKeys[0] === element.key,
            },
            close,
          )}
        </span>
      </MenuItem>
    ));

  const renderItemsWithSections = (
    elements: FixedPositionedDropdownElement<T, K, Sections>[],
    sections: Sections,
  ) => {
    if (!sections) return renderItems(elements);
    const resultsGroupedBySections = groupBy(elements, (r) =>
      "sectionKey" in r ? r.sectionKey : "",
    );

    const sectionsWithResults = sections
      .filter((section) => section.key in resultsGroupedBySections)
      .map((section) => ({
        ...section,
        results: resultsGroupedBySections[section.key],
      }));

    return sectionsWithResults.map((section, i) => (
      <MenuSection
        key={section.key}
        data-loc={`${dataLoc}-section-${section.key}`}
      >
        <MenuHeading
          className={twJoin(
            "pb-1.5 pl-4",
            i === 0 ? "pt-1.5" : "pt-3.5",
            sectionDividers && i > 0 && "border-t border-t-gray-200",
          )}
        >
          {typeof section.displayText === "string" ? (
            <p className="font-inter-semibold-13px text-gray-800">
              {section.displayText}
            </p>
          ) : (
            section.displayText
          )}
        </MenuHeading>
        {renderItems(section.results)}
      </MenuSection>
    ));
  };

  const renderFooterAsItem = () => {
    const footer = renderFooter?.(close);
    return footer ? <MenuItem>{footer}</MenuItem> : undefined;
  };
  const disableButton =
    disabled || (elements.length === 0 && !renderFooter && !renderHeader);

  const containerRef = useRef<HTMLDivElement>(null);
  const menuItemsRef = useRef<HTMLDivElement>(null);
  useOnClickOutside([containerRef, menuItemsRef], () => {
    close();
  });

  return (
    <Menu
      ref={containerRef}
      as="div"
      className={twMerge(
        "text-left font-inter-normal-13px text-gray-800",
        className,
      )}
      data-loc={dataLoc}
    >
      <>
        <MenuButton
          className={twMerge(
            "flex h-full w-full items-center justify-center rounded-lg",
            "border border-gray-200 bg-white",
            !error &&
              "focus-within:border-indigo-600 focus-within:ring-2 focus-within:ring-indigo-600/25 focus:outline-none",
            (multiple ? selectedKeys.length : selectedItems[0]?.value) &&
              "text-gray-500",
            disableButton && "cursor-default bg-gray-50",
            open && "border-indigo-600 outline-none ring-2 ring-indigo-600/25",
            error && "border-red-600 outline-none ring-2 ring-red-400/25",
            buttonClassName,
          )}
          data-loc={buttonDataLoc}
          disabled={disableButton}
          role="button"
          onClick={open ? close : openDropdown}
        >
          {multiple
            ? renderButtonValue(
                selectedItems.map((item) => item?.value),
                open,
                close,
              )
            : renderButtonValue(selectedItems[0]?.value, open, close)}
        </MenuButton>
        <AnimatePresence>
          {open && (
            <MenuItems
              ref={menuItemsRef}
              anchor={{
                to: placement,
                gap: 8,
                offset: placementOffsetX,
                padding: 8,
              }}
              as={MotionDropdownPanel}
              className={twMerge(
                listMatchesButtonWidth && "min-w-[var(--button-width)]",
                itemsClassNames,
              )}
              data-loc={`${dataLoc}-dropdown`}
              portal={portal}
              static
            >
              {renderHeader?.()}
              {sections
                ? renderItemsWithSections(elements, sections)
                : renderItems(elements)}
              {renderFooterAsItem()}
            </MenuItems>
          )}
        </AnimatePresence>
      </>
    </Menu>
  );
};
